/**
 * Audience Segment builder (#seg3) data model — ported from the production
 * monolith (index.html, seg3 IIFE lines 6336-6823). Full-mode Step 3 IS this
 * builder; the static Send-Scope selects are MVP-mode only.
 *
 * Design rule (monolith 6344): "nothing narrows until the user narrows —
 * sparse-field filters start OFF/all-on."
 */

/* pill/option lists: [value, label] (value "" = the neutral/Any/All choice) */
export const AUD_OPTS: [keyof SegState["auds"], string][] = [
  ["L", "Leads"], ["O", "Opportunities"], ["R", "Realtors"], ["H", "Homeowners"],
];
export const LEAD_CREATED: [string, string][] = [
  ["", "All Time"], ["d30", "Last 30 Days"], ["d90", "Last 90 Days"], ["m6", "Last 6 Months"], ["y2", "Last 2 Years"],
];
export const LEAD_STATUS: [keyof SegState["leadStatus"], string][] = [
  ["new", "New & Untouched"], ["waiting", "Waiting for Response"], ["dialogue", "Engaged · Dialogue"], ["appt", "Engaged · Appt Set"],
];
export const PERSONAS: [string, string][] = [
  ["Market", "Market / All"], ["FTHB", "First-Time Homebuyer"], ["MoveUp", "Move-up Buyer"], ["ActiveAdult", "55-Plus"],
];
export const TIMEFRAMES: [keyof SegState["tf"], string][] = [
  ["t03", "0–3 mo"], ["t36", "3–6 mo"], ["t612", "6–12 mo"], ["t12", "12+ mo"],
];
export const PRICE_TIERS: [keyof SegState["ptier"], string][] = [
  ["u3", "Under $300K"], ["t34", "$300–400K"], ["t45", "$400–500K"], ["t56", "$500–600K"],
  ["t68", "$600–800K"], ["t8m", "$800K–$1M"], ["m1", "$1M+"],
];
export const PRODUCTS: [string, string][] = [
  ["", "Any"], ["Single-family", "Single-family"], ["Townhome", "Townhome"], ["Condo", "Condo"], ["Active Adult (55+)", "Active Adult (55+)"],
];
export const BEDS: [string, string][] = [["", "Any"], ["2", "2"], ["3", "3"], ["4", "4"], ["5+", "5+"]];
export const FEATS: string[] = ["Pool", "Home office", "Primary on main", "Smart home", "Large yard", "Gated"];
export const ENGAGE: [string, string][] = [
  ["", "Any"], ["rfi30", "New RFI · 30d"], ["rfi90", "New RFI · 90d"], ["rfi180", "New RFI · 6mo"], ["norfi", "No RFI 6mo+"],
];
export const OPP_SCOPE: [string, string][] = [["comm", "Selected Communities"], ["div", "All Division Opps"]];
/** suppression toggles: [key, label, alwaysOn] */
export const SUPPRESSIONS: [keyof SegState["sup"], string, boolean][] = [
  ["optout", "Opt-outs & do-not-email", true],
  ["assigned", "Already assigned to a community", false],
  ["recent", "Received a marketing email in the last 3 days", false],
  ["stale", "Dormant 12+ months", false],
  ["affiliate", "Exclude affiliate-marketing opt-outs", false],
];

export interface SegState {
  auds: { L: boolean; O: boolean; R: boolean; H: boolean };
  oppScope: string;
  persona: string;
  comm: string;
  aoi: string;
  geoInc: string[];
  geoExc: string[];
  leadStatus: { new: boolean; waiting: boolean; dialogue: boolean; appt: boolean };
  created: string;
  tf: { t03: boolean; t36: boolean; t612: boolean; t12: boolean };
  ptier: { u3: boolean; t34: boolean; t45: boolean; t56: boolean; t68: boolean; t8m: boolean; m1: boolean };
  product: string;
  beds: string;
  feats: string[];
  web: boolean;
  engage: string;
  sup: { assigned: boolean; optout: boolean; recent: boolean; stale: boolean; affiliate: boolean };
  /** live SFMC counts (null = pending) */
  counts?: { reach?: number | null; leads?: number | null; opps?: number | null; realtors?: number | null; ho?: number | null; supp?: number | null };
}

/** REC — the recommended defaults (monolith line 6345). */
export const REC: SegState = {
  auds: { L: true, O: true, R: false, H: false },
  oppScope: "comm", persona: "FTHB", comm: "", aoi: "", geoInc: [], geoExc: [],
  leadStatus: { new: true, waiting: true, dialogue: true, appt: true },
  created: "y2",
  tf: { t03: true, t36: true, t612: true, t12: true },
  ptier: { u3: false, t34: false, t45: false, t56: false, t68: false, t8m: false, m1: false },
  product: "", beds: "", feats: [], web: false, engage: "",
  sup: { assigned: false, optout: true, recent: true, stale: false, affiliate: false },
  counts: {},
};

export const newSeg = (): SegState => JSON.parse(JSON.stringify(REC));
export const isRecommended = (s: SegState) =>
  JSON.stringify({ ...s, counts: undefined }) === JSON.stringify({ ...REC, counts: undefined });

/** audience code, e.g. {L,O}→"LO" (furthest-along order L,O,R,H). */
export const audsCode = (a: SegState["auds"]) => (["L", "O", "R", "H"] as const).filter((k) => a[k]).join("");

const labelsOf = <K extends string>(opts: [K, string][], on: (k: K) => boolean) => opts.filter(([k]) => on(k)).map(([, l]) => l);

/** the checkbox-dropdown face (monolith audsLabel) */
export const audsLabel = (a: SegState["auds"]) =>
  labelsOf(AUD_OPTS, (k) => a[k]).join(", ") || "Choose Audiences";

/* monolith PLABEL + CRLABEL (whoText vocab) */
const PLABEL: Record<string, string> = { Market: "all buyer types", FTHB: "first-time buyers", MoveUp: "move-up buyers", ActiveAdult: "active adult (55+)", Investor: "investors" };
const CRLABEL: Record<string, string> = { d30: "created in last 30 days", d90: "created in last 90 days", m6: "created in last 6 months", y2: "created in last 2 years" };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const escq = (s: string) => s.replace(/"/g, "&quot;");

/** prose description of the audience — VERBATIM port of the monolith's whoText():
 *  most-salient-first bits, CAPPED to 3, the rest folded into a hoverable "+N more".
 *  Returns HTML (the caller renders with dangerouslySetInnerHTML, like innerHTML). */
export function whoText(s: SegState): string {
  const p = cap(PLABEL[s.persona] || s.persona);
  const where = s.comm ? "interested in " + s.comm : "across the " + s.aoi + " area";
  const bits: string[] = [];
  if (s.created && CRLABEL[s.created]) bits.push(CRLABEL[s.created]);
  const lsSel = (Object.keys(s.leadStatus) as (keyof SegState["leadStatus"])[]).filter((k) => s.leadStatus[k]);
  if (lsSel.length && lsSel.length < 4) bits.push(!s.leadStatus.new && lsSel.length === 3 ? "engaged only" : lsSel.length + " lead statuses");
  if (s.engage) {
    const eg = ENGAGE.find((x) => x[0] === s.engage)?.[1];
    if (eg) bits.push(eg.toLowerCase());
  }
  if (!s.tf.t12) {
    const ord: [keyof SegState["tf"], string][] = [["t612", "12 months"], ["t36", "6 months"], ["t03", "3 months"]];
    for (const [k, l] of ord) { if (s.tf[k]) { bits.push("moving within " + l); break; } }
  }
  const pt = (Object.keys(s.ptier) as (keyof SegState["ptier"])[]).filter((k) => s.ptier[k]);
  if (pt.length) bits.push(pt.length + " price tier" + (pt.length > 1 ? "s" : ""));
  if (s.product && s.product !== "Any") bits.push(s.product.toLowerCase());
  if (s.beds && s.beds !== "Any") bits.push(s.beds + "-bed");
  if (s.feats.length) bits.push(s.feats.length + " must-have" + (s.feats.length > 1 ? "s" : ""));
  if (s.web) bits.push("recently active");
  const ex: string[] = [];
  if (s.auds.H) ex.push("homeowners");
  if (s.auds.R) ex.push("realtors");
  if (ex.length) bits.push("+ " + ex.join(", "));
  const CAP = 3, shown = bits.slice(0, CAP), extraN = bits.length - CAP;
  const tail = extraN > 0 ? ', <span title="' + escq(bits.join(", ")) + '" style="border-bottom:1px dotted #9aa;cursor:help">+' + extraN + " more</span>" : "";
  const filt = shown.length ? " — " + shown.join(", ") + tail : "";
  const lead = isRecommended(s) ? "Recommended from your template &amp; inputs:" : "Your customized audience:";
  return `${lead} <b>${p}</b> ${where}${filt}.`;
}

/** map seg state → the AudienceSnapshot the submit path expects (monolith __seg.fields). */
export function segToAudienceSnapshot(s: SegState) {
  return {
    community: s.comm || undefined,
    audType: audsCode(s.auds),
    audiences: labelsOf(AUD_OPTS, (k) => s.auds[k]).join(", ") || undefined,
    opportunityScope: s.auds.O ? (OPP_SCOPE.find(([v]) => v === s.oppScope)?.[1]) : undefined,
    /* ── THE CODE, NOT THE LABEL (2026-08-28, Katy: "I'm missing the Target
       Audience/Buyer Type (used in UTM 2026) field") ────────────────────────────────
       This sent the PERSONA LABEL, and `buyerTypes()` in submit.ts maps through a
       case-sensitive alias table then hard-filters against the Airtable option list.
       None of the four labels survived that trip:

         "First-Time Homebuyer"  vs the option "First-time Homebuyer"  — capital T
         "55-Plus"               vs "Active Adult/55+"                 — no alias
         "Market / All"          vs "All Buyer Types"                  — no alias

       Only "Move-up Buyer" matched, and the default persona is FTHB — so an untouched
       form always shipped an EMPTY column, which is exactly what Katy saw. The CODE
       ("FTHB" / "MoveUp" / "ActiveAdult" / "Market") is what `BUYER_MAP` is keyed on,
       so sending it makes the alias table do its job. The labels are aliased too, in
       submit.ts, so an older payload carrying one still lands. */
    buyerProfile: s.persona || undefined,
    areaOfInterest: s.aoi || undefined,
    leadStatus: labelsOf(LEAD_STATUS, (k) => s.leadStatus[k]),
    createdWindow: LEAD_CREATED.find(([v]) => v === s.created && v !== "")?.[1],
    buyerTimeframe: labelsOf(TIMEFRAMES, (k) => s.tf[k]),
    priceRange: labelsOf(PRICE_TIERS, (k) => s.ptier[k]),
    productType: s.product || undefined,
    bedrooms: s.beds || undefined,
    mustHaves: s.feats,
    engagement: ENGAGE.find(([v]) => v === s.engage && v !== "")?.[1],
    websiteActive: s.web,
    suppressionWindows: SUPPRESSIONS.filter(([k]) => s.sup[k]).map(([, l]) => l),
    estimatedReach: s.counts?.reach ?? undefined,
  };
}
